import { useEffect, useRef, useState } from 'react';
import UseToast from '../UseToast';

const VoiceDetection = ({
  onsessionInfo,
  styles,
  aiFormData,
  isInRangeRef,
  disableSpeechButton,
  isAvatarTalking,
  onCreateSession,
}) => {
  const [audioLevel, setAudioLevel] = useState(0);
  const [isDetectionActive, setIsDetectionActive] = useState(true);
  const [threshold, setThreshold] = useState(5);
  const { showWarningToast } = UseToast();

  // Audio detection refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // State tracking refs
  const isProcessingRef = useRef(false);
  const cooldownTimerRef = useRef(null);
  const thresholdCrossedRef = useRef(false);
  const sessionCheckRef = useRef(null);
 ///   console.log('✅ Seesson 1', onsessionInfo);
  // Fetch threshold from backend
  useEffect(() => {
    const fetchThreshold = async () => {
      try {
        const backendThreshold = aiFormData?.voice_detection_threshold;
        if (backendThreshold !== undefined && backendThreshold !== null) {
          setThreshold(Number(backendThreshold));
          console.log('✅ Voice detection threshold set to:', backendThreshold);
        }
      } catch (error) {
        console.error('❌ Error fetching threshold:', error);
      }
    };

    if (aiFormData) {
      fetchThreshold();
    }
  }, [aiFormData]);

  // Initialize microphone and audio detection
  useEffect(() => {
    const startAudioDetection = async (onsessionInfo) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);

        // Configure analyser for better voice detection
        analyserRef.current.smoothingTimeConstant = 0.3;
        analyserRef.current.fftSize = 2048;

        // Connect nodes
        microphoneRef.current.connect(analyserRef.current);

        // Start detection loop
        detectAudioLevel(onsessionInfo);
        
        console.log('✅ Voice detection initialized successfully');
      } catch (error) {
        console.error('❌ Error accessing microphone:', error);
        showWarningToast('Please enable your microphone for voice detection.');
      }
    };

    startAudioDetection(onsessionInfo);

    return () => {
      // Cleanup
      // if (animationFrameRef.current) {
      //   cancelAnimationFrame(animationFrameRef.current);
      // }
      // if (microphoneRef.current) {
      //   microphoneRef.current.disconnect();
      // }
      // if (analyserRef.current) {
      //   analyserRef.current.disconnect();
      // }
      // if (audioContextRef.current) {
      //   audioContextRef.current.close();
      // }
      // if (cooldownTimerRef.current) {
      //   clearTimeout(cooldownTimerRef.current);
      // }
      // if (sessionCheckRef.current) {
      //   clearTimeout(sessionCheckRef.current);
      // }
    };
  }, []);

  // Monitor avatar speaking state and speech button to pause/resume detection
  useEffect(() => {
    // Pause detection when avatar is talking or speech button is disabled
    if (disableSpeechButton || isAvatarTalking) {
      console.log('🔇 Pausing voice detection (avatar speaking or button disabled)');
      setIsDetectionActive(false);
      isProcessingRef.current = true;
      thresholdCrossedRef.current = false;
    } 
    // Resume detection after avatar finishes and button is enabled
    else if (!disableSpeechButton && !isAvatarTalking && isProcessingRef.current) {
      console.log('⏳ Avatar finished - starting cooldown before resuming detection');
      
      // Clear any existing cooldown
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      
      // Resume detection after 2 seconds cooldown
      cooldownTimerRef.current = setTimeout(() => {
        console.log('✅ Resuming voice detection - ready to listen');
        setIsDetectionActive(true);
        isProcessingRef.current = false;
        thresholdCrossedRef.current = false;
      }, 2000);
    }
  }, [disableSpeechButton, isAvatarTalking]);

  const detectAudioLevel = (onsessionInfo) => {
    if (!analyserRef.current) {
      animationFrameRef.current = requestAnimationFrame(detectAudioLevel);
      return;
    }

    const array = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(array);

    // Calculate average audio level
    const average = array.reduce((a, b) => a + b) / array.length;

    // Normalize to 0-100 scale
    const normalizedLevel = Math.round((average / 255) * 100);
    setAudioLevel(normalizedLevel);

    // Check if voice crosses threshold
    const voiceDetected = normalizedLevel > threshold;

    // Update parent ref for other components
    if (isInRangeRef) {
      isInRangeRef.current = voiceDetected;
    }

    // Auto-trigger speech button when:
    // 1. Voice level exceeds threshold
    // 2. Detection is active (not paused)
    // 3. Not already processing
    // 4. Threshold hasn't been crossed yet (prevent multiple triggers)
    if (voiceDetected && isDetectionActive && !isProcessingRef.current && !thresholdCrossedRef.current) {
      console.log('🎤 Voice threshold crossed! Auto-triggering speech button...', {
        level: normalizedLevel,
        threshold: threshold,
        hasSession: !!onsessionInfo
      });
      
      thresholdCrossedRef.current = true;
      triggerSpeechButton(onsessionInfo);
    }

    // Continue detection loop
    animationFrameRef.current = requestAnimationFrame(detectAudioLevel);
  };

  const triggerSpeechButton = (onsessionInfo) => {
    // Prevent multiple simultaneous triggers
    if (isProcessingRef.current) {
      console.log('⚠️ Already processing, skipping trigger');
      return;
    }

    // Check if session exists - if not, create one first
    if (!onsessionInfo) {
      console.log('⚠️ No active session - creating session first...');
      
      // Mark as processing to prevent multiple triggers
      isProcessingRef.current = true;
      setIsDetectionActive(false);
      
      // Create session if callback provided
      if (onCreateSession) {
        onCreateSession();
        
        // Wait for session to be created, then trigger speech button
        sessionCheckRef.current = setTimeout(() => {
          console.log('🔄 Session should be ready, attempting to trigger speech button...');
          clickSpeechButton();
        }, 2000); // Wait 3 seconds for session creation
      } else {
        console.error('❌ No session and no onCreateSession callback provided');
        // Reset state
        isProcessingRef.current = false;
        setIsDetectionActive(true);
        thresholdCrossedRef.current = false;
      }
      return;
    }

    // Session exists, proceed with triggering
    clickSpeechButton();
  };

  const clickSpeechButton = () => {
    // Mark as processing
    isProcessingRef.current = true;
    setIsDetectionActive(false);

    console.log('🔘 Triggering "Speak Now" button...');

    // Find and click the speech button
    const speechButton = document.getElementById('ai_starttalk');
    
    if (speechButton) {
      // Check if button is visible and enabled
      const isVisible = speechButton.style.display !== 'none';
      const isDisabled = speechButton.hasAttribute('disabled');
      
      if (!isVisible || isDisabled) {
        console.warn('⚠️ Speech button is not ready (hidden or disabled)');
        // Reset state
        isProcessingRef.current = false;
        setIsDetectionActive(true);
        thresholdCrossedRef.current = false;
        return;
      }

      // Trigger click
      setTimeout(() => {
        speechButton.click();
        console.log('✅ Speech button clicked successfully - user can now speak');
      }, 100);
    } else {
      console.error('❌ Speech button (#ai_starttalk) not found in DOM');
      // Reset state if button not found
      isProcessingRef.current = false;
      setIsDetectionActive(true);
      thresholdCrossedRef.current = false;
    }
  };

  return (
    <div id="voice_detection_location" style={styles}>
      {/* {isDetectionActive && (
        <div
          style={{
            padding: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '5px',
            color: 'white',
            fontSize: '12px',
            visibility: aiFormData?.disable_camera_front === 'yes' ? 'hidden' : 'visible',
          }}
        >
          <div style={{ marginBottom: '5px' }}>
            <strong>🎤 Voice Detection Active</strong>
          </div>
          <div style={{ marginBottom: '5px' }}>
            Audio Level: {audioLevel}%
          </div>
          <div style={{ marginBottom: '5px' }}>
            Threshold: {threshold}%
          </div>
          <div
            style={{
              width: '100%',
              height: '10px',
              backgroundColor: '#333',
              borderRadius: '5px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${audioLevel}%`,
                height: '100%',
                backgroundColor: audioLevel > threshold ? '#00ff00' : '#0088ff',
                transition: 'width 0.1s ease-out',
              }}
            />
          </div>
        </div>
      )}
      
      {!isDetectionActive && (
        <div
          style={{
            padding: '10px',
            backgroundColor: 'rgba(255, 165, 0, 0.7)',
            borderRadius: '5px',
            color: 'white',
            fontSize: '12px',
            visibility: aiFormData?.disable_camera_front === 'yes' ? 'hidden' : 'visible',
          }}
        >
          <div>
            <strong>⏸️ Voice Detection Paused</strong>
          </div>
          <div style={{ fontSize: '10px', marginTop: '5px' }}>
            {isAvatarTalking ? 'Avatar is speaking...' : 'Processing...'}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default VoiceDetection;
